import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Check,
  Copy,
  ExternalLink,
  Link2,
  CalendarClock,
  CalendarCheck,
  CheckCircle2,
  CheckCheck,
  UserX,
  ArrowLeftRight,
  RotateCcw,
  Trash2,
  Video,
  AlertCircle,
  Shield,
  X,
} from 'lucide-react';
import { useAuthedApi } from '../../lib/useAuthedApi';
import { useClerkReady } from '../../lib/useClerkReady';
import { MeetingTypeBadge, MEETING_META, asMeetingType } from '../../lib/meetingType';
import LocationManagers from '../../components/LocationManagers';
import QrCodeButton from '../../components/QrCodeButton';

type ApiFn = ReturnType<typeof useAuthedApi>;
type Me = { id: string; name: string; slug: string; managed_location_ids?: string[] };
type Org = { slug: string };
type Booking = {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  bookee_name: string;
  bookee_email: string | null;
  bookee_phone: string | null;
  staff_id: string;
  staff_name: string;
  location_id: string;
  location_name: string;
  service_name: string | null;
  meeting_type: 'video_call' | 'phone_call' | 'in_person' | null;
  meeting_link: string | null;
  notes: string | null;
  checked_in_at: string | null;
  timezone: string;
};
type Connection = {
  provider: 'google' | 'outlook';
  email: string;
  is_connected: number;
  token_expiry: string;
};

type CalView = 'list' | 'month' | 'week' | 'day';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Time-grid (week/day) constants. The default window is 7am–9pm; it auto-
// expands to include any booking that falls outside it.
const HOUR_PX = 48;
const DEFAULT_START_HOUR = 7;
const DEFAULT_END_HOUR = 21;

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 1, 0, 0, 0, 0);
}
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function startOfWeek(d: Date): Date {
  const x = startOfDay(d);
  x.setDate(x.getDate() - x.getDay()); // Sunday-start, matches DAY_LABELS
  return x;
}
function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function minutesInto(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}
function fmtHour(h: number): string {
  const ampm = h < 12 || h === 24 ? 'am' : 'pm';
  const hr = ((h + 11) % 12) + 1;
  return `${hr}${ampm}`;
}

export default function Dashboard() {
  const apiFn = useAuthedApi();
  const { orgRole } = useClerkReady();
  const isAdmin = orgRole === 'org:admin';

  const [me, setMe] = useState<Me | null>(null);
  // Admins and location managers get the cross-staff view + reassign/outcome
  // actions. Manager scope comes from /staff/me (the backend already scopes the
  // bookings list to their location(s)).
  const isManager = (me?.managed_location_ids?.length ?? 0) > 0;
  const canManage = isAdmin || isManager;
  const [org, setOrg] = useState<Org | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [conns, setConns] = useState<Connection[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [view, setView] = useState<CalView>('month');
  const [cursor, setCursor] = useState<Date>(() => startOfMonth(new Date()));
  // Month view's selected day (drives the side panel of that day's appointments).
  const [selected, setSelected] = useState<Date>(() => startOfDay(new Date()));
  const [copied, setCopied] = useState<string | null>(null);
  // Booking id currently mid status-update, so its action buttons disable.
  const [statusBusy, setStatusBusy] = useState<string | null>(null);
  // Location + staff filters for admins and managers (client-side over the
  // loaded bookings; hold the selected id, '' = all). Options come from a
  // scoped roster (filterOptions), not derived from bookings, so a manager can
  // always narrow by any staff member at their location — even one with no
  // bookings yet.
  const [filterLoc, setFilterLoc] = useState('');
  const [filterStaff, setFilterStaff] = useState('');
  const [filterOptions, setFilterOptions] = useState<{
    locations: { id: string; name: string }[];
    staff: { id: string; name: string }[];
  }>({ locations: [], staff: [] });
  // Calendar event → click popover (anchored on desktop, bottom sheet on mobile).
  // We keep the anchor ELEMENT (not a one-time rect) so the popover can track it
  // as the page/grid scrolls.
  const [popover, setPopover] = useState<{ booking: Booking; anchorEl: HTMLElement } | null>(null);
  // Locations this manager runs (for the manager Managers panel). Empty for
  // non-managers. Names come from GET /locations (not admin-gated).
  const [managedLocations, setManagedLocations] = useState<{ id: string; name: string }[]>([]);

  // OAuth callback lands here with ?connected=google / ?error=foo. We surface
  // a one-shot banner and then clear the query param so a refresh doesn't
  // re-show it.
  const [params, setParams] = useSearchParams();
  const connectedParam = params.get('connected');
  const errorParam = params.get('error');
  const errorDescParam = params.get('error_description');

  const loadConnections = useCallback(() => {
    apiFn<Connection[]>('/calendar/connections/me')
      .then(setConns)
      .catch((e) => setErr((e as Error).message));
  }, [apiFn]);

  useEffect(() => {
    Promise.all([apiFn<Me>('/staff/me'), apiFn<Org>('/organizations/me')])
      .then(([m, o]) => {
        setMe(m);
        setOrg(o);
      })
      .catch((e) => setErr((e as Error).message));
    loadConnections();
  }, [apiFn, loadConnections]);

  // Restore this user's preferred default view (per-device). Week/day open on
  // today, not whatever month the cursor defaulted to.
  useEffect(() => {
    if (!me) return;
    const saved = localStorage.getItem(`invitey:calview:${me.id}`);
    if (saved === 'list' || saved === 'month' || saved === 'week' || saved === 'day') {
      setView(saved);
      if (saved === 'week' || saved === 'day') setCursor(startOfDay(new Date()));
    }
  }, [me]);

  function changeView(v: CalView) {
    setView(v);
    // Week/day should land on today, not the 1st of the cursor's month.
    if (v === 'week' || v === 'day') setCursor(startOfDay(new Date()));
    if (me) localStorage.setItem(`invitey:calview:${me.id}`, v);
  }

  // Drilling into a specific day (clicking a month cell or week day-header)
  // switches to Day view for that date. Not persisted — it's navigation, not a
  // default-view choice.
  function openDay(d: Date) {
    setView('day');
    setCursor(startOfDay(d));
  }

  // Always load a month-wide window around the cursor's month; that comfortably
  // covers whatever week or day is visible too.
  const loadBookings = useCallback(() => {
    const from = new Date(startOfMonth(cursor).getTime() - 7 * 24 * 60 * 60_000);
    const to = new Date(endOfMonth(cursor).getTime() + 14 * 24 * 60 * 60_000);
    const url = `/bookings?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`;
    apiFn<Booking[]>(url)
      .then(setBookings)
      .catch((e) => setErr((e as Error).message));
  }, [apiFn, cursor]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  // Location + staff filter options, scoped by the backend (admin → all;
  // manager → their location(s) and the staff there). Only fetched for users
  // who can manage; plain members get no filters.
  useEffect(() => {
    if (!canManage) return;
    apiFn<{ locations: { id: string; name: string }[]; staff: { id: string; name: string }[] }>(
      '/bookings/filter-options',
    )
      .then(setFilterOptions)
      .catch(() => setFilterOptions({ locations: [], staff: [] }));
  }, [apiFn, canManage]);

  // Resolve names for the manager's own locations so they can promote members.
  useEffect(() => {
    const ids = me?.managed_location_ids ?? [];
    if (ids.length === 0) {
      setManagedLocations([]);
      return;
    }
    apiFn<{ id: string; name: string }[]>('/locations')
      .then((locs) =>
        setManagedLocations(
          locs.filter((l) => ids.includes(l.id)).map((l) => ({ id: l.id, name: l.name })),
        ),
      )
      .catch(() => setManagedLocations([]));
  }, [apiFn, me]);

  // Filters narrow the loaded set before it drives the calendar + agenda.
  const visibleBookings = useMemo(
    () =>
      bookings.filter(
        (b) =>
          (!filterLoc || b.location_id === filterLoc) &&
          (!filterStaff || b.staff_id === filterStaff),
      ),
    [bookings, filterLoc, filterStaff],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, Booking[]>();
    for (const b of visibleBookings) {
      if (b.status === 'cancelled') continue;
      const key = ymd(new Date(b.start_time));
      const arr = map.get(key) ?? [];
      arr.push(b);
      map.set(key, arr);
    }
    return map;
  }, [visibleBookings]);

  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const startDow = first.getDay();
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - startDow);
    const cells: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

  // List view = upcoming appointments (next 14 days), sorted. The calendar
  // views render the grid instead, so the list is no longer view-scoped.
  const agenda = useMemo(() => {
    const now = new Date();
    const ceiling = new Date(now.getTime() + 14 * 24 * 60 * 60_000);
    return visibleBookings
      .filter((b) => b.status !== 'cancelled')
      .filter((b) => {
        const t = new Date(b.start_time);
        return t >= now && t <= ceiling;
      })
      .slice()
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [visibleBookings]);

  function navigate(delta: number) {
    if (view === 'month') {
      const d = new Date(cursor);
      d.setMonth(d.getMonth() + delta);
      setCursor(startOfMonth(d));
    } else if (view === 'week') {
      setCursor(addDays(startOfWeek(cursor), delta * 7));
    } else {
      setCursor(addDays(startOfDay(cursor), delta));
    }
  }

  function goToday() {
    const now = new Date();
    setCursor(view === 'month' ? startOfMonth(now) : startOfDay(now));
  }

  // Record an appointment outcome (confirm / complete / no-show / cancel). The
  // backend emits a booking.status_changed event for close-rate + no-show
  // analytics; cancelling also frees the staff calendar hold.
  type BookingStatus = 'confirmed' | 'completed' | 'no_show' | 'cancelled';
  async function updateStatus(id: string, status: BookingStatus) {
    if (
      status === 'cancelled' &&
      !confirm('Cancel this appointment? The calendar hold will be released.')
    ) {
      return;
    }
    setStatusBusy(id);
    setErr(null);
    try {
      await apiFn(`/bookings/${id}`, { method: 'PATCH', body: { status } });
      loadBookings();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setStatusBusy(null);
    }
  }

  async function copy(value: string, key: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    } catch {
      // Clipboard may be blocked; user can still select+copy manually.
    }
  }

  async function startConnect(provider: 'google' | 'outlook') {
    try {
      const { auth_url } = await apiFn<{ auth_url: string }>(
        `/calendar/${provider}/connect`,
      );
      window.location.assign(auth_url);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function disconnect(provider: 'google' | 'outlook') {
    if (!confirm(`Disconnect ${provider}?`)) return;
    await apiFn(`/calendar/${provider}/disconnect`, { method: 'POST' });
    loadConnections();
  }

  function dismissBanner() {
    const next = new URLSearchParams(params);
    next.delete('connected');
    next.delete('error');
    next.delete('error_description');
    setParams(next, { replace: true });
  }

  if (err) return <p className="err">{err}</p>;
  if (!me || !org) return <p className="muted">Loading…</p>;

  const today = new Date();
  const headerLabel =
    view === 'month'
      ? cursor.toLocaleString(undefined, { month: 'long', year: 'numeric' })
      : view === 'day'
        ? cursor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
        : weekLabel(cursor);
  const base = window.location.origin;
  const personalUrl = `${base}/${org.slug}/staff/${me.slug}`;
  const orgUrl = `${base}/${org.slug}`;
  const googleConn = conns?.find((c) => c.provider === 'google');
  const outlookConn = conns?.find((c) => c.provider === 'outlook');
  const connsLoading = conns === null;
  const hasConnectedCalendar = !!conns?.some((c) => c.is_connected === 1);
  const showFilters =
    canManage && (filterOptions.locations.length > 1 || filterOptions.staff.length > 1);
  const gridDays =
    view === 'week'
      ? Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(cursor), i))
      : [startOfDay(cursor)];
  return (
    <div className="dashboard">
      {(connectedParam || errorParam) && (
        <div className={`banner ${errorParam ? 'banner-err' : 'banner-ok'}`}>
          {errorParam ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
          <span>
            {errorParam
              ? `Connect failed: ${errorParam}${errorDescParam ? ` — ${errorDescParam}` : ''}`
              : `${connectedParam} calendar connected.`}
          </span>
          <button className="ghost banner-dismiss" onClick={dismissBanner} aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}

      <div className="card dash-main">
          <div className="cal-header">
            {view !== 'list' && (
              <>
                <button className="ghost" onClick={() => navigate(-1)} aria-label="Previous">
                  <ChevronLeft size={16} />
                </button>
                <h2>
                  <CalendarDays size={16} /> {headerLabel}
                </h2>
                <button className="ghost" onClick={() => navigate(1)} aria-label="Next">
                  <ChevronRight size={16} />
                </button>
              </>
            )}
            {view === 'list' && (
              <h2>
                <CalendarClock size={16} /> Next 14 days
              </h2>
            )}
            <div className="spacer" />
            <div className="seg" role="group" aria-label="View">
              {([
                ['list', 'List'],
                ['day', 'Day'],
                ['week', 'Week'],
                ['month', 'Month'],
              ] as [CalView, string][]).map(([v, label]) => (
                <button
                  key={v}
                  className={view === v ? 'seg-on' : ''}
                  onClick={() => changeView(v)}
                >
                  {label}
                </button>
              ))}
            </div>
            {view !== 'list' && (
              <button className="ghost" onClick={goToday}>
                Today
              </button>
            )}
          </div>

          {showFilters && (
            <div className="cal-filters">
              {filterOptions.locations.length > 1 && (
                <select value={filterLoc} onChange={(e) => setFilterLoc(e.target.value)}>
                  <option value="">All locations</option>
                  {filterOptions.locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              )}
              {filterOptions.staff.length > 1 && (
                <select value={filterStaff} onChange={(e) => setFilterStaff(e.target.value)}>
                  <option value="">All staff</option>
                  {filterOptions.staff.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {view === 'list' ? (
            <div className="agenda-list-wrap">
              {agenda.length === 0 ? (
                <div className="empty-state">
                  <CalendarDays size={24} />
                  <p className="muted">No upcoming appointments.</p>
                </div>
              ) : (
                <ul className="agenda-list">
                  {agenda.map((b) => (
                    <AgendaItem
                      key={b.id}
                      b={b}
                      busy={statusBusy === b.id}
                      canManage={canManage}
                      apiFn={apiFn}
                      onStatus={updateStatus}
                      onReload={loadBookings}
                    />
                  ))}
                </ul>
              )}
            </div>
          ) : view === 'month' ? (
            <div className="month-layout">
              <div className="month-cal">
                <div className="cal-dow">
                  {DAY_LABELS.map((d) => (
                    <span key={d}>{d}</span>
                  ))}
                </div>
                <div className="cal-grid">
                  {grid.map((d) => {
                    const inMonth = d.getMonth() === cursor.getMonth();
                    const isToday = sameDay(d, today);
                    const isSelected = sameDay(d, selected);
                    const count = byDay.get(ymd(d))?.length ?? 0;
                    const classes = [
                      'cal-cell',
                      inMonth ? '' : 'cal-other-month',
                      isToday ? 'cal-today' : '',
                      isSelected ? 'cal-selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <button
                        key={d.toISOString()}
                        className={classes}
                        onClick={() => setSelected(d)}
                        aria-label={
                          d.toDateString() +
                          (count ? `, ${count} appointment${count === 1 ? '' : 's'}` : '')
                        }
                      >
                        <span className="cal-day-num">{d.getDate()}</span>
                        {count > 0 && <span className="cal-dot" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="month-day-panel">
                <h3>
                  {selected.toLocaleDateString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                </h3>
                {(() => {
                  const dayList = (byDay.get(ymd(selected)) ?? [])
                    .slice()
                    .sort((a, b) => a.start_time.localeCompare(b.start_time));
                  return dayList.length === 0 ? (
                    <p className="muted small">No appointments this day.</p>
                  ) : (
                    <ul className="agenda-list">
                      {dayList.map((b) => (
                        <AgendaItem
                          key={b.id}
                          b={b}
                          busy={statusBusy === b.id}
                          canManage={canManage}
                          apiFn={apiFn}
                          onStatus={updateStatus}
                          onReload={loadBookings}
                        />
                      ))}
                    </ul>
                  );
                })()}
              </div>
            </div>
          ) : (
            <TimeGrid
              days={gridDays}
              bookings={visibleBookings}
              canManage={canManage}
              onSelectDay={openDay}
              onEventClick={(booking, el) => setPopover({ booking, anchorEl: el })}
            />
          )}
        </div>

      {isManager && managedLocations.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>
            <Shield size={16} /> Managers
          </h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Promote other members to co-manage your location(s). A manager sees and
            manages every appointment there.
          </p>
          {managedLocations.map((l) => (
            <details key={l.id} className="dev-settings" style={{ marginTop: '0.5rem' }}>
              <summary>{l.name}</summary>
              <div className="dev-settings-body">
                <LocationManagers locationId={l.id} />
              </div>
            </details>
          ))}
        </div>
      )}

      <div className="dashboard-grid">
        <div className="card">
        <h2 style={{ marginTop: 0 }}>
          <Link2 size={16} /> Your booking links
        </h2>
        <p className="muted">
          Your personal link books people directly with you. The org-wide link lets them browse
          and pick a staff member.
        </p>
        <label>
          <span>Personal link (pre-selects you)</span>
          {connsLoading ? (
            <p className="muted small">Checking your calendar connection…</p>
          ) : hasConnectedCalendar ? (
            <div className="url-display">
              <code>{personalUrl}</code>
              <button onClick={() => copy(personalUrl, 'personal')}>
                {copied === 'personal' ? <Check size={14} /> : <Copy size={14} />}
                {copied === 'personal' ? 'Copied' : 'Copy'}
              </button>
              <QrCodeButton url={personalUrl} title={me.name} filename={`${org.slug}-${me.slug}`} />
              <a href={personalUrl} target="_blank" rel="noopener noreferrer" className="btn">
                <ExternalLink size={14} /> Open
              </a>
            </div>
          ) : (
            <div className="url-locked">
              <AlertCircle size={16} />
              <div>
                <strong>Connect a calendar to activate your personal link.</strong>
                <p className="muted small" style={{ margin: '0.25rem 0 0' }}>
                  Without one it shows a “page not found,” so we hide it until it works.
                </p>
              </div>
            </div>
          )}
        </label>
        <label>
          <span>Org-wide link</span>
          <div className="url-display">
            <code>{orgUrl}</code>
            <button onClick={() => copy(orgUrl, 'org')}>
              {copied === 'org' ? <Check size={14} /> : <Copy size={14} />}
              {copied === 'org' ? 'Copied' : 'Copy'}
            </button>
            <QrCodeButton url={orgUrl} title="Org-wide booking link" filename={org.slug} />
            <a href={orgUrl} target="_blank" rel="noopener noreferrer" className="btn">
              <ExternalLink size={14} /> Open
            </a>
          </div>
        </label>
      </div>

      <div className="card" id="calendar-connections" style={{ scrollMarginTop: '80px' }}>
        <h2 style={{ marginTop: 0 }}>
          <CalendarCheck size={16} /> Calendar connections
        </h2>
        <p className="muted">
          Bookings are written here, and your existing events block out slots automatically.
        </p>
        <div className="connections-grid">
          <ProviderTile
            name="Google Calendar"
            icon={<GoogleMark />}
            conn={googleConn}
            onConnect={() => startConnect('google')}
            onDisconnect={() => disconnect('google')}
          />
          <ProviderTile
            name="Outlook"
            icon={<OutlookMark />}
            conn={outlookConn}
            onConnect={() => startConnect('outlook')}
            onDisconnect={() => disconnect('outlook')}
          />
        </div>
      </div>
      </div>

      {popover && (
        <AppointmentPopover
          booking={popover.booking}
          anchorEl={popover.anchorEl}
          canManage={canManage}
          apiFn={apiFn}
          busy={statusBusy === popover.booking.id}
          onStatus={updateStatus}
          onReload={loadBookings}
          onClose={() => setPopover(null)}
        />
      )}
    </div>
  );
}

function weekLabel(cursor: Date): string {
  const ws = startOfWeek(cursor);
  const we = addDays(ws, 6);
  const sameMonth = ws.getMonth() === we.getMonth();
  const start = ws.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const end = we.toLocaleDateString(
    undefined,
    sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' },
  );
  return `${start} – ${end}, ${we.getFullYear()}`;
}

// Week / day time-grid: hour rows on the left, one column per day, appointments
// positioned by start time and sized by duration. Clicking a block focuses that
// day in the agenda (where the actions live).
function TimeGrid({
  days,
  bookings,
  canManage,
  onSelectDay,
  onEventClick,
}: {
  days: Date[];
  bookings: Booking[];
  canManage: boolean;
  onSelectDay: (d: Date) => void;
  onEventClick: (b: Booking, el: HTMLElement) => void;
}) {
  const dayKeys = days.map(ymd);
  const evs = bookings.filter(
    (b) => b.status !== 'cancelled' && dayKeys.includes(ymd(new Date(b.start_time))),
  );

  let startHour = DEFAULT_START_HOUR;
  let endHour = DEFAULT_END_HOUR;
  for (const b of evs) {
    const s = new Date(b.start_time);
    const e = new Date(b.end_time);
    startHour = Math.min(startHour, s.getHours());
    endHour = Math.max(endHour, e.getHours() + (e.getMinutes() > 0 ? 1 : 0));
  }
  startHour = Math.max(0, startHour);
  endHour = Math.min(24, Math.max(endHour, startHour + 1));
  const hours: number[] = [];
  for (let h = startHour; h <= endHour; h++) hours.push(h);
  const bodyHeight = (endHour - startHour) * HOUR_PX;
  const winStartMin = startHour * 60;
  const today = new Date();

  return (
    <div className="cal-timegrid-wrap">
      <div className={`cal-timegrid${days.length === 1 ? ' cal-timegrid-day' : ''}`}>
        <div className="cal-timecol">
          <div className="cal-daycol-head" aria-hidden="true" />
          <div className="cal-timecol-body" style={{ height: bodyHeight }}>
            {hours.map((h) => (
              <div key={h} className="cal-hour-label" style={{ top: (h - startHour) * HOUR_PX }}>
                {fmtHour(h)}
              </div>
            ))}
          </div>
        </div>
        {days.map((day) => {
          const key = ymd(day);
          const dayEvs = evs
            .filter((b) => ymd(new Date(b.start_time)) === key)
            .sort((a, b) => a.start_time.localeCompare(b.start_time));
          const isToday = sameDay(day, today);
          return (
            <div key={key} className="cal-daycol">
              <button
                className={`cal-daycol-head cal-daycol-head-btn${isToday ? ' cal-today-head' : ''}`}
                onClick={() => onSelectDay(day)}
              >
                {day.toLocaleDateString(undefined, { weekday: 'short' })} <b>{day.getDate()}</b>
              </button>
              <div className="cal-daycol-body" style={{ height: bodyHeight }}>
                {hours.map((h) => (
                  <div
                    key={h}
                    className="cal-hour-line"
                    style={{ top: (h - startHour) * HOUR_PX }}
                  />
                ))}
                {dayEvs.map((b) => {
                  const s = new Date(b.start_time);
                  const e = new Date(b.end_time);
                  const top = ((minutesInto(s) - winStartMin) / 60) * HOUR_PX;
                  const height = Math.max(
                    20,
                    ((e.getTime() - s.getTime()) / 3_600_000) * HOUR_PX,
                  );
                  const mt = asMeetingType(b.meeting_type);
                  const startTime = s.toLocaleTimeString(undefined, {
                    hour: 'numeric',
                    minute: '2-digit',
                  });
                  // Short blocks (≈30–45 min) can't fit stacked rows, so collapse
                  // time + name onto one line; taller blocks keep the full layout.
                  const compact = height < 48;
                  return (
                    <button
                      key={b.id}
                      className={`cal-event${compact ? ' cal-event-compact' : ''}`}
                      style={{ top, height, ...(mt ? { background: MEETING_META[mt].color } : {}) }}
                      onClick={(e) => onEventClick(b, e.currentTarget)}
                      title={`${startTime} · ${b.bookee_name}${mt ? ` · ${MEETING_META[mt].label}` : ''}${canManage ? ` · with ${b.staff_name}` : ''}`}
                    >
                      {compact ? (
                        <span className="cal-event-line">
                          {startTime} · {b.bookee_name}
                        </span>
                      ) : (
                        <>
                          <span className="cal-event-time">{startTime}</span>
                          <span className="cal-event-name">{b.bookee_name}</span>
                          {canManage && <span className="cal-event-staff">with {b.staff_name}</span>}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Agenda list wrapper — a card around the shared appointment body.
function AgendaItem(props: {
  b: Booking;
  busy: boolean;
  canManage: boolean;
  apiFn: ApiFn;
  onStatus: (id: string, status: 'confirmed' | 'completed' | 'no_show' | 'cancelled') => void;
  onReload: () => void;
}) {
  return (
    <li className="appt-card">
      <AppointmentBody {...props} />
    </li>
  );
}

// Anchored click popover (desktop) / bottom sheet (mobile) showing one
// appointment's details + actions. The corner ✕ only closes; acting on the
// appointment also closes it (via onAfterAction).
function AppointmentPopover({
  booking,
  anchorEl,
  canManage,
  apiFn,
  busy,
  onStatus,
  onReload,
  onClose,
}: {
  booking: Booking;
  anchorEl: HTMLElement;
  canManage: boolean;
  apiFn: ApiFn;
  busy: boolean;
  onStatus: (id: string, status: 'confirmed' | 'completed' | 'no_show' | 'cancelled') => void;
  onReload: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isMobile =
    typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Anchor to the clicked block's *live* rect (flip left / clamp so it stays
  // onscreen). Re-run on scroll/resize so the popover tracks the time slot as
  // the page or the horizontally-scrolling grid moves.
  const reposition = useCallback(() => {
    if (isMobile || !ref.current) return;
    const pad = 8;
    const r = anchorEl.getBoundingClientRect();
    const { offsetWidth: w, offsetHeight: h } = ref.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.right + pad;
    if (left + w > vw - pad) left = r.left - w - pad;
    if (left < pad) left = pad;
    let top = r.top;
    if (top + h > vh - pad) top = Math.max(pad, vh - h - pad);
    if (top < pad) top = pad;
    setPos({ left, top });
  }, [anchorEl, isMobile]);

  useLayoutEffect(() => {
    reposition();
  }, [reposition]);

  useEffect(() => {
    if (isMobile) return;
    // capture:true catches scrolls on the inner (horizontally-scrolling) grid
    // container too, since scroll events don't bubble.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [reposition, isMobile]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const style: CSSProperties = isMobile
    ? {}
    : {
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        visibility: pos ? 'visible' : 'hidden',
      };

  return (
    <>
      <div className="appt-popover-backdrop" onClick={onClose} />
      <div
        ref={ref}
        className={`appt-popover${isMobile ? ' appt-popover-sheet' : ''}`}
        style={style}
        role="dialog"
        aria-modal="true"
        aria-label="Appointment details"
        tabIndex={-1}
      >
        <button className="appt-popover-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <AppointmentBody
          b={booking}
          busy={busy}
          canManage={canManage}
          apiFn={apiFn}
          onStatus={onStatus}
          onReload={onReload}
          onAfterAction={onClose}
        />
      </div>
    </>
  );
}

// Shared appointment detail + actions, used by both the agenda list and the
// click popover. Cancel lives in the action row (a corner ✕ would read as
// "close" in the popover). `onAfterAction` lets the popover close on any action.
function AppointmentBody({
  b,
  busy,
  canManage,
  apiFn,
  onStatus,
  onReload,
  onAfterAction,
}: {
  b: Booking;
  busy: boolean;
  canManage: boolean;
  apiFn: ApiFn;
  onStatus: (id: string, status: 'confirmed' | 'completed' | 'no_show' | 'cancelled') => void;
  onReload: () => void;
  onAfterAction?: () => void;
}) {
  const start = new Date(b.start_time);
  const end = new Date(b.end_time);
  const timeStr = `${start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} – ${end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  const dateStr = start.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  // "Manageable" = a live, upcoming appointment. 'rescheduled' counts: it's
  // just a booking that was moved, still active and re-manageable (matches the
  // customer /manage flow, which also treats it as active). Excluding it here
  // was why a booking couldn't be rescheduled/cancelled a second time.
  const active =
    b.status === 'scheduled' || b.status === 'confirmed' || b.status === 'rescheduled';
  const [reassignOpen, setReassignOpen] = useState(false);
  const [eligible, setEligible] = useState<{ id: string; name: string }[] | null>(null);
  const [target, setTarget] = useState('');
  const [rBusy, setRBusy] = useState(false);
  const [rErr, setRErr] = useState<string | null>(null);

  const REASSIGN_ERR: Record<string, string> = {
    slot_taken: 'That staff member already has a booking then.',
    staff_off: 'That staff member is off then.',
    ineligible_staff: "That staff member can't cover this service at this location.",
    same_staff: "That's already the assigned staff member.",
  };

  // Status change + (for the popover) close after acting.
  function act(status: 'confirmed' | 'completed' | 'no_show' | 'cancelled') {
    onStatus(b.id, status);
    onAfterAction?.();
  }

  async function openReassign() {
    setReassignOpen(true);
    setRErr(null);
    setEligible(null);
    try {
      const list = await apiFn<{ id: string; name: string }[]>(`/bookings/${b.id}/eligible-staff`);
      setEligible(list);
      if (list[0]) setTarget(list[0].id);
    } catch (e) {
      setRErr((e as Error).message);
    }
  }

  async function confirmReassign() {
    if (!target) return;
    setRBusy(true);
    setRErr(null);
    try {
      await apiFn(`/bookings/${b.id}/reassign`, { method: 'POST', body: { staff_id: target } });
      setReassignOpen(false);
      onReload();
      onAfterAction?.();
    } catch (e) {
      const code = (e as { body?: { error?: string } }).body?.error;
      setRErr((code && REASSIGN_ERR[code]) || (e as Error).message);
    } finally {
      setRBusy(false);
    }
  }

  // Reschedule (admin / manager): move the booking to a new time.
  const [reschedOpen, setReschedOpen] = useState(false);
  const [newTime, setNewTime] = useState('');
  const [sBusy, setSBusy] = useState(false);
  const [sErr, setSErr] = useState<string | null>(null);

  const RESCHED_ERR: Record<string, string> = {
    slot_taken: 'That time is already taken — pick another.',
    staff_off: 'The staff member is off then.',
    outside_hours: "That's outside available hours.",
    past_slot: 'Pick a time in the future.',
  };

  function openResched() {
    // Prefill with the current start, in the browser's local time.
    const d = new Date(b.start_time);
    const pad = (n: number) => String(n).padStart(2, '0');
    setNewTime(
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
    );
    setSErr(null);
    setReschedOpen(true);
  }

  async function confirmResched() {
    if (!newTime) return;
    setSBusy(true);
    setSErr(null);
    try {
      // datetime-local is parsed as local time; toISOString() gives the instant.
      await apiFn(`/bookings/${b.id}/reschedule`, {
        method: 'POST',
        body: { start_time: new Date(newTime).toISOString(), timezone: b.timezone },
      });
      setReschedOpen(false);
      onReload();
      onAfterAction?.();
    } catch (e) {
      const code = (e as { body?: { error?: string } }).body?.error;
      setSErr((code && RESCHED_ERR[code]) || (e as Error).message);
    } finally {
      setSBusy(false);
    }
  }

  const mt = asMeetingType(b.meeting_type);
  const checkedInAt = b.checked_in_at
    ? new Date(b.checked_in_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : null;

  return (
    <>
      <div className="appt-head">
        <span className="appt-date">{dateStr}</span>
        <span className="appt-time">{timeStr}</span>
        {mt && <MeetingTypeBadge type={mt} />}
      </div>

      <div className="appt-name">{b.bookee_name}</div>
      {canManage && <div className="appt-with">with {b.staff_name}</div>}
      <div className="muted small appt-svc">
        {b.service_name ?? 'Appointment'} · {b.location_name}
      </div>
      {(b.bookee_email || b.bookee_phone) && (
        <div className="muted small">
          {b.bookee_email}
          {b.bookee_email && b.bookee_phone ? ' · ' : ''}
          {b.bookee_phone}
        </div>
      )}

      {b.meeting_link && (
        <a
          className="appt-join"
          href={b.meeting_link}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Video size={14} /> Join the meeting room
        </a>
      )}

      {(checkedInAt || b.status === 'confirmed' || b.status === 'rescheduled' || !active) && (
        <div className="appt-chips">
          {checkedInAt && (
            <span className="status-chip" style={{ background: '#dcfce7', color: '#166534' }}>
              <CheckCircle2 size={12} style={{ verticalAlign: '-2px' }} /> Checked in {checkedInAt}
            </span>
          )}
          {b.status === 'rescheduled' && (
            <span className="status-chip" style={{ background: '#fef3c7', color: '#92400e' }}>
              Rescheduled
            </span>
          )}
          {b.status === 'confirmed' && (
            <span className="status-chip" style={{ background: '#e0e7ff', color: '#3730a3' }}>
              Confirmed
            </span>
          )}
          {!active && <span className="status-chip">{b.status.replace('_', '-')}</span>}
        </div>
      )}

      {active && (
        <div className="appt-foot">
          <div className="appt-actions" role="group" aria-label="Appointment actions">
            <button
              disabled={busy}
              onClick={() => act('completed')}
              aria-label="Mark completed"
              title="Mark completed"
            >
              <CheckCheck size={16} />
            </button>
            <button
              disabled={busy}
              onClick={() => act('no_show')}
              aria-label="Mark no-show"
              title="Mark no-show"
            >
              <UserX size={16} />
            </button>
            {canManage && !reassignOpen && (
              <button disabled={busy} onClick={openReassign} aria-label="Reassign" title="Reassign">
                <ArrowLeftRight size={16} />
              </button>
            )}
            {canManage && !reschedOpen && (
              <button disabled={busy} onClick={openResched} aria-label="Reschedule" title="Reschedule">
                <CalendarClock size={16} />
              </button>
            )}
          </div>
          <button
            className="danger small appt-cancel-btn"
            disabled={busy}
            onClick={() => act('cancelled')}
            title="Cancel appointment"
          >
            <Trash2 size={13} /> Cancel
          </button>
        </div>
      )}

      {!active && (b.status === 'no_show' || b.status === 'completed') && (
        <div className="appt-foot">
          <button
            className="ghost small appt-reassign"
            disabled={busy}
            onClick={() => act('confirmed')}
            title="Undo — set back to active"
          >
            <RotateCcw size={13} /> Undo
          </button>
        </div>
      )}

      {canManage && reassignOpen && (
        <div className="reassign-row">
          {eligible === null ? (
            <span className="muted small">Loading staff…</span>
          ) : eligible.length === 0 ? (
            <span className="muted small">No other eligible staff for this service/location.</span>
          ) : (
            <>
              <select value={target} onChange={(e) => setTarget(e.target.value)} disabled={rBusy}>
                {eligible.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <button className="primary small" disabled={rBusy || !target} onClick={confirmReassign}>
                {rBusy ? 'Moving…' : 'Move'}
              </button>
            </>
          )}
          <button
            className="ghost small"
            disabled={rBusy}
            onClick={() => {
              setReassignOpen(false);
              setRErr(null);
            }}
          >
            Cancel
          </button>
          {rErr && <span className="err small" style={{ flexBasis: '100%' }}>{rErr}</span>}
        </div>
      )}

      {canManage && reschedOpen && (
        <div className="reassign-row">
          <input
            type="datetime-local"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
            disabled={sBusy}
          />
          <button className="primary small" disabled={sBusy || !newTime} onClick={confirmResched}>
            {sBusy ? 'Moving…' : 'Move'}
          </button>
          <button
            className="ghost small"
            disabled={sBusy}
            onClick={() => {
              setReschedOpen(false);
              setSErr(null);
            }}
          >
            Cancel
          </button>
          {sErr && <span className="err small" style={{ flexBasis: '100%' }}>{sErr}</span>}
        </div>
      )}
    </>
  );
}

function ProviderTile({
  name,
  icon,
  conn,
  onConnect,
  onDisconnect,
}: {
  name: string;
  icon: ReactNode;
  conn: Connection | undefined;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  return (
    <div className="provider-tile">
      <div className="provider-head">
        <span className="provider-icon">{icon}</span>
        <div className="provider-name">{name}</div>
      </div>
      {conn ? (
        <>
          <p className="muted small">{conn.email}</p>
          <span className="provider-status">
            <CheckCircle2 size={14} /> Connected
          </span>
          <button className="danger" onClick={onDisconnect}>
            Disconnect
          </button>
        </>
      ) : (
        <button className="primary" onClick={onConnect}>
          Connect {name}
        </button>
      )}
    </div>
  );
}

// Multi-color provider marks; kept inline so we don't pull a logo dependency
// and so they pick up dark-mode-friendly outlines if we ever add one.
function GoogleMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8a12 12 0 1 1 7.9-21l5.7-5.7A20 20 0 1 0 44 24c0-1.2-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="m6.3 14.7 6.6 4.8A12 12 0 0 1 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7A20 20 0 0 0 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3A12 12 0 0 1 12.7 28L6 33A20 20 0 0 0 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.5l6.3 5.3C40 35 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

function OutlookMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#0364B8" d="M28 8h14a2 2 0 0 1 2 2v28a2 2 0 0 1-2 2H28V8z" />
      <path fill="#0078D4" d="M28 14h16v20H28z" />
      <path fill="#28A8EA" d="M28 14h16v10H28z" />
      <path fill="#0358A7" d="M4 10v28l24 4V6z" />
      <path
        fill="#fff"
        d="M16 18c-3.3 0-6 3.1-6 7s2.7 7 6 7 6-3.1 6-7-2.7-7-6-7zm0 11c-1.9 0-3.4-1.8-3.4-4S14.1 21 16 21s3.4 1.8 3.4 4S17.9 29 16 29z"
      />
    </svg>
  );
}
